import { C8NodeType } from "./node";
import { Operator } from "./operators";
import { C8AttributeKey, C8AttributeValue } from "./value";

/**
 * Version history
 * 	1: 2016-Feb-26 (5.X?)
 * 		rework filters that reference column matrix group
 * 		rework semi-colon separated filter values with match condition
 * 	2: 2017-Feb-16 (6.1?)
 * 		migrate date EQ and NE filters
 *	3: 2024-Feb-07 (7.9)
 *		combine filters with the same attribute key and operator (multi-value filters)
 *	4: 2024-Mar-XX (7.9.1)
 *		new custom view dialog
 *		attributeKeys now persisted with matrix and attribute groups
 * 		filterSettings can have groups
 *  5: 2024-July-17 (7.10)
 *		convert filters with empty values to blank / not blank filters in older version custom views  
 */
export const enum CustomViewVersion {
	V2016 = 1,
	V2017 = 2,
	V79 = 3,
	V791 = 4,
	V710 = 5,
};

/** version number added to custom view Options to indicate what transformations need to be done when loading custom view */
export const CUSTOM_VIEW_VERSION = CustomViewVersion.V710;

export interface ICustomViewAttributeGroup {
	/** key for group if from an attribute or matrix group */
	groupKey?: C8AttributeKey;

	/** attribute keys within group */
	keys: C8AttributeKey[];

	/** target type of matrix if a matrix group */
	matrixTarget?: C8NodeType;

	/** (custom) name of group */
	name?: string;
}

export type ICustomViewAttribute = C8AttributeKey | ICustomViewAttributeGroup;

export interface ICustomViewFilterCondition {
	/** id of the attribute */
	key: C8AttributeKey;

	/** operator of this query */
	op: Operator;

	/** determines if the query is relative */
	relative?: boolean;

	/** value of the condition; if relative, then type is C8AttributeKey */
	value: C8AttributeKey | C8AttributeValue;
}

export interface ICustomViewFilterConditionV1 extends ICustomViewFilterCondition {
	/** indicates if condition is grouped with next condtion */
	group?: boolean;
}

export interface ICustomViewFilterGroup {
	/** filter conditions inside of the filter group */
	conditions: ICustomViewFilterCondition[];

	/** determines if the filter group query is an AND query */
	isAND: boolean;
}

export interface ICustomViewFilter extends Omit<ICustomViewFilterV1, "C"> {
	/** list of (grouped) filter conditions (FILTER_CONDITIONS) */
	C: (ICustomViewFilterGroup | ICustomViewFilterCondition)[];
}

export interface ICustomViewFilterV1 {
	/** list of filter conditions (FILTER_CONDITIONS) */
	C: ICustomViewFilterConditionV1[];

	/** determines if the filter uses floating dates (FILTER_FLOATING_DATES) */
	FD?: boolean;

	/** locale used when filtering for localized attributes (FILTER_LOCALE) */
	L?: string;

	/** determines if the filter query is an OR query (FILTER_OR) */
	OR: boolean;
}

export type ICustomViewFilterSettingsV1 = Record<string, ICustomViewFilterV1>;

export type ICustomViewFilterSettings =  Record<string, ICustomViewFilter>;

export interface ICustomViewGroup {
	/** first group level's attribute key or column attribute group's key */
	key1?: C8AttributeKey;

	/** second group level's attribute key or row attribute grouop's key */
	key2?: C8AttributeKey;

	/** indicates if we expand by default on first group level attribute */
	expand1?: boolean;

	/** indicates if we expand by default on second group level attribute */
	expand2?: boolean;
}

export interface ICustomViewGroupV1 {
	/** determines if expand by default */
	expand: boolean;

	/** id of the attribute in the query */
	key: C8AttributeKey;
}

export interface ICustomViewReport {
	/** report function */
	func: string;

	/** id of the attribute in the query */
	key: C8AttributeKey;

	/** id of the attribute for weighted average function */
	key2?: C8AttributeKey;
}

export interface ICustomViewSort {
	/** determines if sorting is done in descending order or ascending order */
	// FIX: this is persisted as "desc", but in memory is "descending"
	descending: boolean;

	/** id of the attribute in the query*/
	key: C8AttributeKey;
}
